import { ASSET_LIABILITY, accountTypeToName } from './types';
import type { AccountType, AssetLiability } from './types';

export const isPlaidInvestmentAccount = (account: {
  account_type?: string | null;
  has_investments?: boolean | null;
}): boolean => {
  const accountType = String(account.account_type || '').toLowerCase();
  return account.has_investments === true || accountType.startsWith('investment');
};

export const selectPlaidDisplayBalance = (account: {
  account_type?: string | null;
  current_balance?: number | null;
  available_balance?: number | null;
  asset_or_liability?: AssetLiability | string | null;
  has_investments?: boolean | null;
}): number => {
  // available_balance on a credit line is the unused credit, not the debt.
  if (account.asset_or_liability === ASSET_LIABILITY.LIABILITY) {
    return Number(account.current_balance ?? 0);
  }

  if (isPlaidInvestmentAccount(account)) {
    return Number(account.current_balance ?? account.available_balance ?? 0);
  }

  return Number(account.available_balance ?? account.current_balance ?? 0);
};

/** Match firm-data valuation while retaining reported balances on account rows. */
export const selectPlaidWealthValue = (
  account: Parameters<typeof selectPlaidDisplayBalance>[0] & {
    source?: string | null;
    status?: string | null;
  },
): number => {
  if (
    account.source === 'firm-data' &&
    ['pending', 'inactive', 'closed'].includes(account.status?.trim().toLowerCase() ?? '')
  ) {
    return 0;
  }
  return selectPlaidDisplayBalance(account);
};

const plaidAccountTypeLabels: Record<string, string> = {
  'depository/checking': 'Checking',
  'depository/savings': 'Savings',
  'depository/cd': 'Certificate of Deposit',
  'depository/cash_management': 'Cash Management',
  'depository/money_market': 'Money Market',
  'depository/hsa': 'Health Savings Account',
  'investment/401k': '401(k)',
  'investment/ira': 'IRA',
  'credit/credit_card': 'Credit Card',
  'loan/mortgage': 'Mortgage',
  'loan/student': 'Student Loan',
};

function formatPlaidAccountType(accountType: string): string {
  const normalized = accountType.trim().toLowerCase();
  if (!normalized) {
    return accountType;
  }

  const mapped = plaidAccountTypeLabels[normalized];
  if (mapped) {
    return mapped;
  }

  if (normalized.includes('/')) {
    const [, subtype] = normalized.split('/', 2);
    if (subtype) {
      return subtype
        .split(/[_\s]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    }
  }

  return accountType;
}

// firm-data's own custodian-agnostic account_type vocabulary — deliberately
// separate from plaidAccountTypeLabels (Plaid's "category/subtype" strings)
// and from accountTypeToName (Apex's own goal vocabulary, e.g. "JUST_INVEST").
// Any account synced into firm-data, from any custodian, uses these flat
// values instead — see plaid-cash-integration's own _firm_data_account_type
// for the canonical list this mirrors.
const firmDataAccountTypeLabels: Record<string, string> = {
  checking: 'Checking',
  savings: 'Savings',
  money_market: 'Money Market',
  cd: 'Certificate of Deposit',
  taxable_brokerage: 'Taxable Brokerage',
  traditional_ira: 'Traditional IRA',
  roth_ira: 'Roth IRA',
  '401k': '401(k)',
  roth_401k: 'Roth 401(k)',
  '403b': '403(b)',
  '457': '457',
  sep_ira: 'SEP IRA',
  simple_ira: 'SIMPLE IRA',
  '529': '529',
  hsa: 'Health Savings Account',
  pension: 'Pension',
  annuity: 'Annuity',
  other: 'Other',
};

function formatFirmDataAccountType(accountType: string): string | null {
  return firmDataAccountTypeLabels[accountType.trim().toLowerCase()] ?? null;
}

function normalizeSavingsLabel(label: string): string {
  return label.trim().toLowerCase() === 'saving' ? 'Savings' : label;
}

// Formats an account_type string for display regardless of which vocabulary
// it came from — Plaid's "category/subtype" strings, firm-data's own flat
// custodian-agnostic vocabulary, or Apex's goal-based AccountType enum.
// accountTypeToName (Apex's own formatter) returns undefined for anything
// outside its enum, so it must be tried last and its result guarded — never
// call it directly on an account_type of unknown origin.
export function formatAccountType(accountType: string | null | undefined): string {
  // Callers pass account types straight off API payloads from three
  // different sources; a missing one should render as nothing, not crash
  // the page that was showing the account.
  if (!accountType) {
    return '';
  }

  const plaidFriendlyName = formatPlaidAccountType(accountType);
  if (plaidFriendlyName !== accountType) {
    return normalizeSavingsLabel(plaidFriendlyName);
  }

  const firmDataFriendlyName = formatFirmDataAccountType(accountType);
  if (firmDataFriendlyName) {
    return normalizeSavingsLabel(firmDataFriendlyName);
  }

  const friendlyName = accountTypeToName(accountType as AccountType);
  return normalizeSavingsLabel(friendlyName || accountType);
}

const GENERIC_BANK_ACCOUNT_NAMES = new Set(['checking', 'savings', 'saving']);

const isGenericBankAccountName = (name: string, typeLabel: string): boolean => {
  const normalized = name.trim().toLowerCase();
  if (!normalized) {
    return true;
  }
  if (GENERIC_BANK_ACCOUNT_NAMES.has(normalized)) {
    return true;
  }
  return Boolean(typeLabel) && normalized === typeLabel.trim().toLowerCase();
};

export const formatExternalBankAccountTitle = (account: {
  institution?: string | null;
  institution_name?: string | null;
  name?: string | null;
  type?: string | null;
  account_type?: string | null;
}): string => {
  const bankName = (account.institution_name ?? account.institution ?? '').trim();
  const typeLabel = formatAccountType(account.account_type ?? account.type);
  const accountName = (account.name ?? '').trim().replace(/^Plaid\s+/i, '');

  if (!isGenericBankAccountName(accountName, typeLabel)) {
    if (bankName && accountName.toLowerCase().startsWith(bankName.toLowerCase())) {
      return accountName;
    }
    if (bankName) {
      return `${bankName} ${accountName}`;
    }
    return accountName;
  }

  if (bankName && typeLabel) {
    return `${bankName} ${typeLabel}`;
  }
  return bankName || typeLabel || accountName || 'Linked bank';
};
